import _ from "lodash";
import React, { useState } from "react";
import { Modal, Button } from "@toddle-edu/ds-web";
import { useI18n } from "Hooks";
import {
  forwardInteractiveMessage,
  forwardMessage,
  getMessagingV2ReduxState,
} from "MessagingV2/modules/MessagingModule";

import ChatAndChannelDualPannel from "./ChatAndChannelDualPannel";
import CustomMessage from "./CustomMessage";
import classes from "./ForwardMessageContainer.scss";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import CustomProgressSteps from "UIComponents/CustomProgressSteps";
import { connect } from "react-redux";
import { useChatOptions } from "MessagingV2/providers/ChatOptionsProvider";
import { MESSAGE_TYPE, MESSAGING_SECTIONS } from "MessagingV2/modules/Utils";
import { getFormattedUserName } from "Utils/GetFormattedUserName";
import { getDefaultNameSortPreference } from "modules/CommonSelectors";

const getStepConfig = ({ selectedConversations, t, isInteractiveMessage }) => {
  return {
    STEP_1: {
      key: "STEP_1",
      id: "STEP_1",
      nextButtonLabel: isInteractiveMessage ? "common:forward" : "common:next",
      cancelButtonLabel: "common:cancel",
      header: "common:forward_message",
      style: {
        width: "700px",
        height: "680px",
      },
      subTitle: null,
      modalBodyStyle: {
        padding: 0,
        overflow: "hidden",
      },
    },
    STEP_2: {
      key: "STEP_2",
      id: "STEP_2",
      nextButtonLabel: "common:forward",
      cancelButtonLabel: "common:back",
      header: "common:add_a_meesage_if_you_like",
      style: {
        width: "480px",
        height: "min-content",
      },
      subTitle: t("common:conversation_selected_count", {
        count: selectedConversations,
      }),
      modalBodyStyle: {
        paddingBlock: "24px",
        maxHeight: "356px",
      },
    },
  };
};

const StepModalWrapper = props => {
  const {
    step,
    selectedEntity,
    setSelectedEntity,
    customMessage,
    setCustomMessage,
    message,
    selectedConversations,
    entityDetails,
    setEntityDetails,
    isInteractiveMessage,
    personalChatReceiverUserId,
  } = props;

  const { t } = useI18n(["common"]);

  const STEP_CONFIG = getStepConfig({ selectedConversations, t });

  switch (step) {
    case STEP_CONFIG.STEP_1.key:
      return (
        <ChatAndChannelDualPannel
          selectedEntity={selectedEntity}
          setSelectedEntity={setSelectedEntity}
          entityDetails={entityDetails}
          setEntityDetails={setEntityDetails}
          hideUsersList={isInteractiveMessage}
          personalChatReceiverUserId={personalChatReceiverUserId}
        />
      );
    case STEP_CONFIG.STEP_2.key:
      return (
        <CustomMessage
          customMessage={customMessage}
          setCustomMessage={setCustomMessage}
          parentMessage={message}
        />
      );
    default:
      return null;
  }
};

const ForwardMessageContainer = props => {
  const {
    onClose,
    message,
    forwardMessage,
    forwardInteractiveMessage,
    selectedUserTypes,
    selectedChannelTypes,
    selectedSection,
    defaultStudentNameSortPreference,
    personalChatReceiverUserId,
  } = props;
  const [selectedEntity, setSelectedEntity] = useState({
    CHANNELS: new Set(),
    USERS: new Set(),
  });
  const [entityDetails, setEntityDetails] = useState({
    CHANNELS: {},
    USERS: {},
  });

  const messageType = _.get(message, "type", "");

  const isInteractiveMessage = _.isEqual(messageType, MESSAGE_TYPE.INTERACTION);

  const [customMessage, setCustomMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useI18n(["common"]);

  const selectedConversations =
    _.size(selectedEntity.CHANNELS) + _.size(selectedEntity.USERS);
  const STEP_CONFIG = getStepConfig({
    selectedConversations,
    t,
    isInteractiveMessage,
  });
  const STEPS = [STEP_CONFIG.STEP_1, STEP_CONFIG.STEP_2];
  const [step, setStep] = useState(_.first(STEPS).key);
  const stepConfig = _.get(STEP_CONFIG, step, {});

  const { chatId } = useChatOptions();

  const onStepChange = async params => {
    switch (params) {
      case "FORWARD":
        {
          if (isInteractiveMessage) {
            if (_.isEmpty(selectedEntity.CHANNELS)) return;
            const channelIds = [...selectedEntity.CHANNELS];
            setIsLoading(true);
            const attachment = _.first(_.get(message, "attachments", []));
            const interaction = _.get(attachment, "interaction", {});
            const interactionId = _.get(interaction, "id");

            const firstChannelId = _.first(channelIds);
            const firstChannelDetails = _.get(entityDetails, [
              "CHANNELS",
              firstChannelId,
            ]);
            await forwardInteractiveMessage({
              channelIds,
              interactionId,
              firstEntityName: _.get(firstChannelDetails, "label", ""),
            });
            setIsLoading(false);
            onClose();
          }
          const firstChannelId = _.first([...selectedEntity.CHANNELS]);
          const firstUserId = _.first([...selectedEntity.USERS]);

          const firstChannelDetails = _.get(entityDetails, [
            "CHANNELS",
            firstChannelId,
          ]);
          const firstUserDetails = _.get(entityDetails, ["USERS", firstUserId]);

          const firstUserOrChannelName = firstUserDetails
            ? getFormattedUserName({
                user: firstUserDetails,
                orderBy: defaultStudentNameSortPreference,
              })
            : firstChannelDetails
            ? _.get(firstChannelDetails, "label", "")
            : "";

          if (_.isEqual(step, STEP_CONFIG.STEP_2.key)) {
            setIsLoading(true);
            await forwardMessage({
              message,
              selectedEntity,
              customMessage,
              selectedChatId: chatId,
              selectedSection,
              selectedChannelTypes,
              selectedUserTypes,
              firstEntityName: firstUserOrChannelName,
            });
            setIsLoading(false);

            onClose();
            return;
          }
          setStep(STEP_CONFIG.STEP_2.key);
        }
        break;
      case "BACK": {
        if (_.isEqual(step, STEP_CONFIG.STEP_1.key)) {
          onClose();
          return;
        }
        setStep(STEP_CONFIG.STEP_1.key);
      }
    }
  };

  const isNextButtonDisabled = () => {
    switch (step) {
      case STEP_CONFIG.STEP_1.key:
        return selectedConversations === 0;
      default:
        return false;
    }
  };

  return (
    <Modal
      height={stepConfig.style.height}
      width={stepConfig.style.width}
      isOpen={true}
      onClose={onClose}
    >
      <Modal.Header
        title={t(stepConfig.header)}
        subtitle={stepConfig.subTitle}
        isClosable
        hasSeparator={true}
      />
      <CustomProgressSteps
        shouldDisplayLabel={false}
        steps={STEPS}
        currentStep={step}
        borderWidth={4}
      />
      <Modal.Body
        style={stepConfig.modalBodyStyle}
        isScrollable={_.isEqual(step, STEP_CONFIG.STEP_1.key) ? false : true}
      >
        <StepModalWrapper
          step={step}
          isInteractiveMessage={isInteractiveMessage}
          selectedEntity={selectedEntity}
          setSelectedEntity={setSelectedEntity}
          customMessage={customMessage}
          setCustomMessage={setCustomMessage}
          message={message}
          selectedConversations={selectedConversations}
          entityDetails={entityDetails}
          setEntityDetails={setEntityDetails}
          personalChatReceiverUserId={personalChatReceiverUserId}
        />
      </Modal.Body>
      <Modal.Footer hasSeparator={true}>
        <div className={classes.footerContainer}>
          <Button
            size="large"
            variant="outlined-subtle"
            onClick={() => onStepChange("BACK")}
          >
            {t(stepConfig.cancelButtonLabel)}
          </Button>
          <Button
            size="large"
            variant="progressive"
            onClick={() => onStepChange("FORWARD")}
            disabled={isNextButtonDisabled()}
          >
            {t(stepConfig.nextButtonLabel)}
          </Button>
        </div>
      </Modal.Footer>
      {isLoading ? <FullScreenLoader /> : null}
    </Modal>
  );
};

const mapStateToProps = state => {
  const messagingState = getMessagingV2ReduxState(state);
  const selectedSectionV2 = _.get(state, "messagingV2.selectedSectionV2");

  return {
    selectedUserTypes: _.get(
      messagingState,
      [MESSAGING_SECTIONS.CHAT, "selectedUserTypes"],
      []
    ),
    selectedChannelTypes: _.get(
      messagingState,
      [MESSAGING_SECTIONS.CHANNELS, "selectedChannelTypes"],
      []
    ),
    selectedSection: selectedSectionV2,
    defaultStudentNameSortPreference: getDefaultNameSortPreference(state),
  };
};

const mapActionCreators = {
  forwardMessage,
  forwardInteractiveMessage,
};

export default connect(
  mapStateToProps,
  mapActionCreators
)(ForwardMessageContainer);
